<div class="detail-wrap">
  <div class="detail-header">
    <div class="header-chips">
      <span class="chip" [class]="priorityClass(ticket().priority)">{{ ticket().priority }}</span>
      <span class="chip" [class]="statusClass(ticket().status)">{{ ticket().status }}</span>
    </div>
    <button class="dialog-close" mat-dialog-close><mat-icon>close</mat-icon></button>
  </div>

  <div class="detail-body">
    <h2 class="detail-title">{{ ticket().title }}</h2>

    <div class="meta-grid">
      <span class="meta-key">Project</span>
      <span class="meta-val">{{ ticket().project_name }}</span>

      @if (!isCicd && !isGcpAccess && !isFileUpload && !isDnsMapping && ticket().environment) {
        <span class="meta-key">Environment</span>
        <span class="meta-val env-badge">{{ ticket().environment.toUpperCase() }}</span>
      }

      <span class="meta-key">Raised by</span>
      <span class="meta-val user-cell">
        @if (ticket().raised_by?.profile_picture) {
          <img class="avatar avatar--sm" [src]="ticket().raised_by!.profile_picture!" referrerpolicy="no-referrer" alt="" />
        } @else {
          <span class="avatar avatar--sm">{{ ticket().raised_by?.name?.charAt(0) }}</span>
        }
        {{ ticket().raised_by?.name }}
      </span>

      <span class="meta-key">Assigned to</span>
      <span class="meta-val user-cell">
        @if (ticket().assigned_to) {
          @if (ticket().assigned_to!.profile_picture) {
            <img class="avatar avatar--sm" [src]="ticket().assigned_to!.profile_picture!" referrerpolicy="no-referrer" alt="" />
          } @else {
            <span class="avatar avatar--sm">{{ ticket().assigned_to!.name.charAt(0) }}</span>
          }
          {{ ticket().assigned_to!.name }}
        } @else {
          <span class="unassigned">Unassigned</span>
        }
      </span>

      <span class="meta-key">Created</span>
      <span class="meta-val">{{ ticket().created_at | date: 'MMM d, y h:mm a' }}</span>

      <span class="meta-key">Updated</span>
      <span class="meta-val">{{ ticket().updated_at | date: 'MMM d, y h:mm a' }}</span>

      @if (ticket().estimated_cost) {
        <span class="meta-key">Monthly Cost</span>
        <span class="meta-val estimated-cost">{{ formatCost(ticket().estimated_cost) }}</span>
      }
    </div>

    @if (ticket().description) {
      <div class="section-label">Description</div>
      <p class="description-text">{{ ticket().description }}</p>
    }

    @if (ticket().status === 'Rejected' && ticket().rejection_reason) {
      <div class="outcome-banner outcome-banner--rejected">
        <mat-icon>cancel</mat-icon>
        <div>
          <div class="outcome-label">Rejection Reason</div>
          <div class="outcome-text">{{ ticket().rejection_reason }}</div>
        </div>
      </div>
    }

    @if (ticket().status === 'Approved' && ticket().estimated_cost) {
      <div class="outcome-banner outcome-banner--approved">
        <mat-icon>check_circle</mat-icon>
        <div>
          <div class="outcome-label">Approved — Estimated Monthly Cost</div>
          <div class="outcome-text">{{ formatCost(ticket().estimated_cost) }}</div>
        </div>
      </div>
    }

    <!-- Request details — sub-type aware -->
    @if (isCicd) {
      <div class="section-label">CI/CD Configuration</div>
      <div class="req-detail-block">
        <div class="req-row">
          <span class="req-key">Repository URL</span>
          @if (firstService?.repo_url) {
            <a class="req-val req-link" [href]="firstService!.repo_url" target="_blank" rel="noopener">
              {{ firstService!.repo_url }}
            </a>
          } @else { <span class="req-val">—</span> }
        </div>
        <div class="req-row">
          <span class="req-key">Branch</span>
          <code class="req-code">{{ firstService?.branch ?? '—' }}</code>
        </div>
        <div class="req-row">
          <span class="req-key">Approvals Required</span>
          <span class="req-val">{{ firstService?.approvals_required ?? '—' }}</span>
        </div>
      </div>
    } @else if (isGcpAccess) {
      <div class="section-label">GCP Access Request</div>
      <div class="req-detail-block">
        <div class="req-row">
          <span class="req-key">GCP Project ID</span>
          <code class="req-code">{{ firstService?.gcp_project_id ?? '—' }}</code>
        </div>
        <div class="req-row req-row--top">
          <span class="req-key">Members</span>
          <div class="req-tags">
            @for (m of (firstService?.members ?? []); track m) {
              <span class="req-tag">{{ m }}</span>
            }
          </div>
        </div>
      </div>
    } @else if (isFileUpload) {
      <div class="section-label">File Upload Exception</div>
      <div class="req-detail-block">
        <div class="req-row">
          <span class="req-key">Service URL</span>
          @if (firstService?.url) {
            <a class="req-val req-link" [href]="firstService!.url" target="_blank" rel="noopener">
              {{ firstService!.url }}
            </a>
          } @else { <span class="req-val">—</span> }
        </div>
        <div class="req-row req-row--top">
          <span class="req-key">Members</span>
          <div class="req-tags">
            @for (m of (firstService?.members ?? []); track m) {
              <span class="req-tag">{{ m }}</span>
            }
          </div>
        </div>
      </div>
    } @else if (isDnsMapping) {
      <div class="section-label">DNS Mapping</div>
      <div class="req-tags req-tags--standalone">
        @for (d of (firstService?.domains ?? []); track d) {
          <span class="req-tag req-tag--domain">{{ d }}</span>
        }
      </div>
    } @else {
      <!-- Setup New Services: cloud services table -->
      <div class="section-label">Services</div>
      @if (firstService?.gcp_project_id) {
        <div class="req-detail-block" style="margin-bottom:14px;">
          <div class="req-row">
            <span class="req-key">GCP Project ID</span>
            <code class="req-code">{{ firstService!.gcp_project_id }}</code>
          </div>
        </div>
      }
      <table class="services-table">
        <thead>
          <tr>
            <th>Service</th>
            <th>Resource Name</th>
            <th>Pods</th>
          </tr>
        </thead>
        <tbody>
          @for (svc of ticket().services; track $index) {
            @if (svc.service === 'GKE' && svc.pod_groups?.length) {
              @for (pg of svc.pod_groups!; track pg.name) {
                <tr>
                  <td>{{ svc.service }} / {{ pg.name }}</td>
                  <td class="resource-name">{{ pg.resource_name }}</td>
                  <td>{{ pg.count }}</td>
                </tr>
              }
            } @else if (svc.service === 'Cloud SQL') {
              <tr>
                <td>{{ svc.service }} <span class="db-badge">{{ svc.database_type ?? 'postgresql' }}</span></td>
                <td class="resource-name">{{ svc.resource_name }}</td>
                <td>—</td>
              </tr>
            } @else {
              <tr>
                <td>{{ svc.service }}</td>
                <td class="resource-name">{{ svc.resource_name }}</td>
                <td>—</td>
              </tr>
            }
          }
        </tbody>
      </table>
    }

    <!-- Approve / Reject (assignee or admin, only while actionable) -->
    @if (canApproveReject) {
      <div class="approval-section">
        <div class="section-label">Action Required</div>
        <div class="approval-btns">
          <button class="btn-action btn-action--approve"
                  [class.btn-action--active]="approvalAction() === 'approve'"
                  (click)="openApproval('approve')">
            <mat-icon>check_circle</mat-icon> Approve
          </button>
          <button class="btn-action btn-action--reject"
                  [class.btn-action--active]="approvalAction() === 'reject'"
                  (click)="openApproval('reject')">
            <mat-icon>cancel</mat-icon> Reject
          </button>
        </div>

        @if (approvalAction() === 'approve') {
          <div class="action-form">
            @if (isSetupNewServices) {
              <label class="action-label">
                Estimated Monthly Cost <span class="req">*</span>
              </label>
              <div class="cost-input-wrap">
                <span class="cost-prefix">$</span>
                <input class="field-input cost-field" type="number" min="0" step="1"
                       [ngModel]="estimatedCostNum()"
                       (ngModelChange)="estimatedCostNum.set($event)"
                       placeholder="0" />
                <span class="cost-suffix">/ month</span>
              </div>
            } @else {
              <p class="action-hint-standalone">Confirm you want to approve this request.</p>
            }
            <div class="action-form-footer">
              <button class="btn-action-cancel" (click)="approvalAction.set(null)">Cancel</button>
              <button class="btn-confirm btn-confirm--approve"
                      [disabled]="approveDisabled"
                      (click)="confirmAction()">
                {{ actioning() ? 'Approving…' : 'Confirm Approval' }}
              </button>
            </div>
          </div>
        }

        @if (approvalAction() === 'reject') {
          <div class="action-form">
            <label class="action-label">Rejection Reason <span class="req">*</span></label>
            <textarea class="field-input field-textarea" rows="3"
                      [(ngModel)]="rejectionReason"
                      placeholder="Explain why this request is being rejected…"></textarea>
            <div class="action-form-footer">
              <button class="btn-action-cancel" (click)="approvalAction.set(null)">Cancel</button>
              <button class="btn-confirm btn-confirm--reject"
                      [disabled]="rejectDisabled"
                      (click)="confirmAction()">
                {{ actioning() ? 'Rejecting…' : 'Confirm Rejection' }}
              </button>
            </div>
          </div>
        }
      </div>
    }

  </div>

  <div class="detail-footer">
    <button class="btn-download btn-download--md" [disabled]="downloadingMd()" (click)="downloadMarkdown()">
      <mat-icon>description</mat-icon>
      {{ downloadingMd() ? 'Generating…' : 'Download Markdown' }}
    </button>
    @if (isSetupNewServices) {
      <button class="btn-download" [disabled]="downloading()" (click)="downloadTerraform()">
        <mat-icon>download</mat-icon>
        {{ downloading() ? 'Generating…' : 'Download Terraform' }}
      </button>
    }
    <button mat-button mat-dialog-close>Close</button>
  </div>
</div>
